import React from "react";
import { Outlet, NavLink, useNavigate, Link } from "react-router-dom";
import { Flame, Users, MessageCircle, CalendarDays, LogOut, Sparkles } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const tabs = [
  { to: "/app/discover", label: "Discover", icon: Flame, testid: "tab-discover" },
  { to: "/app/matches", label: "Matches", icon: Users, testid: "tab-matches" },
  { to: "/app/chat", label: "Chat", icon: MessageCircle, testid: "tab-chat" },
  { to: "/app/schedule", label: "Schedule", icon: CalendarDays, testid: "tab-schedule" },
  { to: "/app/community", label: "Crew", icon: Sparkles, testid: "tab-community" },
];

const AppShell = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();

  const onLogout = async () => {
    await logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white flex flex-col" data-testid="app-shell">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#0A0A0A]/85 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-3xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
          <Link to="/app/discover" className="flex items-center gap-2" data-testid="app-brand">
            <span className="w-2 h-2 rounded-full bg-[#C6FF00] shadow-[0_0_18px_#C6FF00]" />
            <span className="font-heading text-2xl tracking-wide">
              SWOLE<span className="text-[#C6FF00]">MATES</span>
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/app/settings"
              data-testid="app-avatar-link"
              className="block"
              aria-label="Settings"
            >
              {user?.photo_url ? (
                <img
                  src={user.photo_url}
                  alt={user.name}
                  className="w-9 h-9 rounded-full object-cover border border-white/10 hover:border-[#C6FF00] transition-colors"
                />
              ) : (
                <div className="w-9 h-9 rounded-full bg-[#C6FF00] text-[#0A0A0A] flex items-center justify-center font-bold">
                  {(user?.name || "?").charAt(0).toUpperCase()}
                </div>
              )}
            </Link>
            <button
              onClick={onLogout}
              data-testid="app-logout-btn"
              className="text-neutral-400 hover:text-[#C6FF00] transition-colors"
              aria-label="Sign out"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 max-w-3xl w-full mx-auto px-5 md:px-8 py-6 pb-28">
        <Outlet />
      </main>

      {/* Bottom nav */}
      <nav
        className="fixed bottom-0 left-0 right-0 z-40 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-white/5"
        data-testid="bottom-nav"
      >
        <div className="max-w-3xl mx-auto px-2 grid grid-cols-5">
          {tabs.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-3 transition-colors ${
                  isActive ? "text-[#C6FF00]" : "text-neutral-500 hover:text-white"
                }`
              }
            >
              <t.icon size={20} />
              <span className="text-[9px] sm:text-[10px] uppercase tracking-[0.12em] sm:tracking-[0.2em] font-semibold whitespace-nowrap">{t.label}</span>
            </NavLink>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default AppShell;
